/**
 * エラーステータスコード別の日本語メッセージマッピング
 */
export const getErrorMessageByStatus = (
  status: number,
  defaultMessage?: string
): string => {
  switch (status) {
    case 400:
    case 404:
      return "必要な情報が不足しています";
    case 401:
      return "認証に失敗しました";
    case 403:
      return "この操作を実行する権限がありません";
    case 409:
      return "既に登録済みのデータです";
    case 422:
      return "入力値が正しくありません";
    case 500:
    case 502:
    case 503:
    case 504:
      return "サーバーエラーが発生しました";
    default:
      return defaultMessage || "エラーが発生しました";
  }
};

/**
 * エラーレスポンスから適切な日本語メッセージを取得
 */
export const getLocalizedErrorMessage = (
  error: any,
  defaultMessage?: string
): string => {
  // ネットワークエラー
  if (!error.response) {
    return "ネットワークエラーが発生しました";
  }

  const status = error.response?.status;
  const serverMessage = error.response?.data?.message;

  // サーバーからのメッセージがある場合は詳細な解析を行う
  if (serverMessage && typeof serverMessage === "string") {
    // 一般的なエラーメッセージパターンをチェック
    if (
      serverMessage.includes("User with this email already exists") ||
      serverMessage.includes("email already exists") ||
      serverMessage.includes("Email already in use")
    ) {
      return "すでに登録済みのメールアドレスです";
    }

    if (
      serverMessage.includes("User not found") ||
      serverMessage.includes("Not found")
    ) {
      return "指定されたユーザーが見つかりません";
    }

    if (
      serverMessage.includes("Invalid credentials") ||
      serverMessage.includes("Invalid email or password") ||
      serverMessage.includes("Authentication failed")
    ) {
      return "メールアドレスまたはパスワードが正しくありません";
    }

    if (
      serverMessage.includes("Access denied") ||
      serverMessage.includes("Forbidden") ||
      serverMessage.includes("Permission denied")
    ) {
      return "この操作を実行する権限がありません";
    }

    if (
      serverMessage.includes("Validation failed") ||
      serverMessage.includes("Invalid input") ||
      serverMessage.includes("Bad request")
    ) {
      return "入力値が正しくありません";
    }

    // OCR関連のエラーメッセージパターンをチェック
    if (serverMessage.includes("Unable to process uploaded file")) {
      return "ファイルの処理に失敗しました";
    }

    if (serverMessage.includes("Unsupported file format")) {
      return "サポートされていないファイル形式です";
    }

    if (
      serverMessage.includes("Invalid file buffer") ||
      serverMessage.includes("Buffer is empty")
    ) {
      return "ファイルが破損しているか、読み込みに失敗しました";
    }

    if (serverMessage.includes("File size exceeds limit")) {
      return "ファイルサイズが大きすぎます";
    }

    if (
      serverMessage.includes(
        "Azure Document Intelligence credentials not configured"
      )
    ) {
      return "一時的にサービスが利用できません";
    }

    if (
      serverMessage.includes(
        "Failed to initialize Azure Document Intelligence client"
      )
    ) {
      return "一時的にサービスが利用できません";
    }

    if (
      serverMessage.includes("Document analysis timeout") ||
      serverMessage.includes("too large or complex")
    ) {
      return "ファイルの処理でタイムアウトしました";
    }

    if (
      serverMessage.includes("Invalid request") ||
      serverMessage.includes("corrupted file") ||
      serverMessage.includes("exceeding Azure limits")
    ) {
      return "ファイルが破損しているか、サイズが大きすぎます";
    }

    if (serverMessage.includes("Empty or invalid buffer provided")) {
      return "ファイルが読み込みに失敗しました";
    }

    if (serverMessage.includes("Unknown error")) {
      return "予期しないエラーが発生しました";
    }

    // 上記のパターンに該当しない場合はそのままサーバーメッセージを返す
    return serverMessage;
  }

  // サーバーメッセージが無い場合のみ、ステータスコード別メッセージにフォールバック
  return getErrorMessageByStatus(status, defaultMessage);
};

/**
 * 操作別のデフォルトエラーメッセージ
 */
export const getOperationErrorMessage = (
  operation: string,
  status?: number,
  error?: any
): string => {
  const baseMessage = getErrorMessageByStatus(status || 0);

  // 特定の操作に対する具体的なメッセージ（409、500エラーの場合）
  if (status === 409 || status === 500) {
    switch (operation) {
      case "create-customer":
        return "すでに登録済みのメールアドレスです";
      case "create-employee":
        return "すでに登録済みのメールアドレスです";
      case "create-property":
        return "同じ物件が既に登録されています";
      case "delete-customer":
        return "選択された顧客は既に削除されているか、削除できません";
      case "smart-extraction":
        return "スマート抽出に失敗しました";
      default:
        return baseMessage;
    }
  }

  // 他の操作の詳細なエラーメッセージ（ステータスコード別）
  switch (operation) {
    case "create-customer":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 422) {
        return "メールアドレスの形式が正しくないか、既に登録済みです";
      }
      return "顧客の登録に失敗しました";

    case "update-customer":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 404) {
        return "更新対象の顧客が見つかりません";
      }
      if (status === 422) {
        return "メールアドレスの形式が正しくないか、既に使用されています";
      }
      return "顧客情報の更新に失敗しました";

    case "delete-customer":
      if (status === 404) {
        return "削除対象の顧客が見つかりません";
      }
      return "顧客の削除に失敗しました";

    case "create-employee":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 422) {
        return "メールアドレスの形式が正しくないか、既に登録済みです";
      }
      return "従業員の登録に失敗しました";

    case "update-employee":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 404) {
        return "更新対象の従業員が見つかりません";
      }
      if (status === 422) {
        return "メールアドレスの形式が正しくないか、既に使用されています";
      }
      return "従業員情報の更新に失敗しました";

    case "delete-employee":
      if (status === 404) {
        return "削除対象の従業員が見つかりません";
      }
      return "従業員の削除に失敗しました";

    case "create-property":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 422) {
        return "物件情報の形式が正しくありません";
      }
      return "物件の登録に失敗しました";

    case "update-property":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 404) {
        return "更新対象の物件が見つかりません";
      }
      if (status === 422) {
        return "物件情報の形式が正しくありません";
      }
      return "物件情報の更新に失敗しました";

    case "delete-property":
      if (status === 404) {
        return "削除対象の物件が見つかりません";
      }
      return "物件の削除に失敗しました";

    case "create-inquiry":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 422) {
        return "問い合わせ情報の形式が正しくありません";
      }
      return "問い合わせの登録に失敗しました";

    case "update-inquiry":
      if (status === 400) {
        return "入力内容に不備があります。必須項目を確認してください";
      }
      if (status === 404) {
        return "更新対象の問い合わせが見つかりません";
      }
      return "問い合わせの更新に失敗しました";

    case "login":
      if (status === 401) {
        return "メールアドレスまたはパスワードが正しくありません";
      }
      if (status === 403) {
        return "アカウントがロックされています。管理者にお問い合わせください";
      }
      return "ログインに失敗しました";

    case "password-reset":
      if (status === 404) {
        return "指定されたメールアドレスが見つかりません";
      }
      return "パスワード変更に失敗しました";

    case "file-upload":
      if (status === 400) {
        return "ファイル形式またはサイズに問題があります";
      }
      if (status === 413) {
        return "ファイルサイズが大きすぎます";
      }
      return "ファイルのアップロードに失敗しました";

    case "delete-inquiry-history":
      if (status === 500) {
        return "問い合わせ履歴の削除に失敗しました";
      }
      return "問い合わせ履歴の削除に失敗しました";

    case "delete-inquiry":
      if (status === 404) {
        return "削除対象の問い合わせが見つかりません";
      }
      return "問い合わせの削除に失敗しました";
  }

  // OCR関連のエラーは詳細なメッセージを返す
  if (operation === "smart-extraction") {
    const errorMessage = error?.response?.data?.message || error?.message || "";

    // ファイル処理エラー
    if (errorMessage.includes("Unable to process uploaded file")) {
      return "ファイルの処理に失敗しました";
    }

    // サポートされていないファイル形式
    if (errorMessage.includes("Unsupported file format")) {
      return "サポートされていないファイル形式です";
    }

    // 空のファイルバッファ
    if (
      errorMessage.includes("Invalid file buffer") ||
      errorMessage.includes("Buffer is empty")
    ) {
      return "ファイルが破損しているか、読み込みに失敗しました";
    }

    // ファイルサイズ超過
    if (errorMessage.includes("File size exceeds limit")) {
      return "ファイルサイズが大きすぎます";
    }

    // Azure資格情報未設定
    if (errorMessage.includes("Azure credentials not configured")) {
      return "一時的にサービスが利用できません";
    }

    // Azureクライアント初期化失敗
    if (errorMessage.includes("Failed to initialize Azure client")) {
      return "一時的にサービスが利用できません";
    }

    // タイムアウトエラー
    if (errorMessage.includes("Document analysis timeout")) {
      return "ファイルの処理でタイムアウトしました";
    }

    // 無効なリクエスト（破損ファイル、サイズ超過等）
    if (
      errorMessage.includes("Invalid request") ||
      errorMessage.includes("corrupted file")
    ) {
      return "ファイルが破損しているか、サイズが大きすぎます";
    }

    // 空または無効なバッファ
    if (errorMessage.includes("Empty or invalid buffer")) {
      return "ファイルの読み込みに失敗しました";
    }

    // 不明なエラー
    if (errorMessage.includes("Unknown error")) {
      return "予期しないエラーが発生しました";
    }

    // ステータスコード別のフォールバック
    if (status === 400) {
      return "ファイル形式またはサイズに問題があります";
    }

    return "スマート抽出に失敗しました。ファイル形式やサイズをご確認ください";
  }

  return baseMessage;
};
